import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Button,
    Alert,
    CircularProgress,
    useTheme as useMuiTheme,
    useMediaQuery,
    Grid,
    IconButton,
    Tooltip,
    Paper
} from '@mui/material';
import {
    Storage as StorageIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBackups, createBackup, downloadBackup, deleteBackup } from '../../store/slices/settingsSlice';
import { useTheme } from '../../contexts/ThemeContext';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import Toast from '../../components/Toast';
import useToast from '../../hooks/useToast';

const DatabaseBackups = () => {
    const muiTheme = useMuiTheme();
    const { theme: appTheme } = useTheme();
    const dispatch = useAppDispatch();
    const { backups, backupsLoading, error } = useAppSelector((state) => state.settings);
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const { toast, showSuccess, showError, hideToast } = useToast();

    const [deleteDialog, setDeleteDialog] = useState(false);
    const [backupToDelete, setBackupToDelete] = useState<string | null>(null);
    const [creatingBackup, setCreatingBackup] = useState(false);

    useEffect(() => {
        dispatch(fetchBackups());
    }, [dispatch]);

    const handleCreateBackup = async () => {
        setCreatingBackup(true);
        try {
            await dispatch(createBackup()).unwrap();
            showSuccess('Database backup created successfully');
        } catch (err: any) {
            showError(err?.message || 'Failed to create backup');
        } finally {
            setCreatingBackup(false);
        }
    };

    const handleDownload = async (filename: string) => {
        try {
            await dispatch(downloadBackup(filename)).unwrap();
        } catch (err: any) {
            showError(err?.message || 'Failed to download backup');
        }
    };

    const handleDeleteClick = (filename: string) => {
        setBackupToDelete(filename);
        setDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (backupToDelete) {
            try {
                await dispatch(deleteBackup(backupToDelete)).unwrap();
                showSuccess('Backup deleted successfully');
            } catch (err: any) {
                showError(err?.message || 'Failed to delete backup');
            }
        }
        setDeleteDialog(false);
        setBackupToDelete(null);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    return (
        <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Header */}
            <Box sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 2
            }}>
                <Box>
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        sx={{
                            color: appTheme.palette.text.primary,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <StorageIcon />
                        Database Backups
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Manage your database backup files
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={creatingBackup ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                    onClick={handleCreateBackup}
                    disabled={creatingBackup}
                    sx={{
                        bgcolor: appTheme.palette.success.main,
                        '&:hover': {
                            bgcolor: appTheme.palette.success.dark,
                        }
                    }}
                >
                    {creatingBackup ? 'Creating Backup...' : 'Backup Database'}
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Backups Grid */}
            <Card sx={{
                bgcolor: muiTheme.palette.background.paper,
                border: `1px solid ${muiTheme.palette.divider}`
            }}>
                <CardHeader
                    title="Available Backups"
                    subheader={`${backups.length} backup file(s) found`}
                    sx={{
                        bgcolor: muiTheme.palette.action.hover,
                        '& .MuiCardHeader-title': {
                            fontWeight: 600,
                            fontSize: '1.1rem'
                        }
                    }}
                />
                <CardContent>
                    {backupsLoading && backups.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : backups.length === 0 ? (
                        <Box sx={{
                            textAlign: 'center',
                            py: 6,
                            color: appTheme.palette.text.secondary
                        }}>
                            <StorageIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                            <Typography variant="h6">No Backups Found</Typography>
                            <Typography variant="body2">
                                Click "Backup Database" to create your first backup
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {backups.map((backup) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={backup.filename}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            border: `1px solid ${muiTheme.palette.divider}`,
                                            borderRadius: 2,
                                            textAlign: 'center',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                borderColor: appTheme.palette.secondary.main,
                                                boxShadow: 1
                                            }
                                        }}
                                    >
                                        <FileIcon
                                            sx={{
                                                fontSize: 48,
                                                color: appTheme.palette.secondary.main,
                                                mb: 1
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                wordBreak: 'break-all',
                                                mb: 0.5
                                            }}
                                        >
                                            {backup.filename}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {backup.size}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                            {formatDate(backup.created_at)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                            <Tooltip title="Download">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDownload(backup.filename)}
                                                    sx={{ color: appTheme.palette.info.main }}
                                                >
                                                    <DownloadIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteClick(backup.filename)}
                                                    sx={{ color: muiTheme.palette.error.main }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </CardContent>
            </Card>

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialog}
                title="Delete Backup"
                message={`Are you sure you want to delete "${backupToDelete}"? This action cannot be undone.`}
                onConfirm={handleDeleteConfirm}
                onCancel={() => {
                    setDeleteDialog(false);
                    setBackupToDelete(null);
                }}
                severity="error"
            />

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default DatabaseBackups;
